import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';

export interface DrawerHeaderProps {
  title?: string;
  onClose: () => void;
  onResetDefault?: () => void;
  onSaveAsDefault?: () => void;
  resetDefaultText?: string;
  saveText?: string;
  isSaving?: boolean;
  showSaveAsDefault?: boolean;
  showResetToDefault?: boolean;
  showMoreFilters?: boolean;
  onToggleMoreFilters?: () => void;
  moreFiltersButtonRef?: React.RefObject<HTMLButtonElement>;
}

const DrawerHeader: React.FC<DrawerHeaderProps> = ({
  title = 'Filters',
  onClose,
  onResetDefault,
  onSaveAsDefault,
  saveText = 'SAVE AS DEFAULT',
  resetDefaultText = 'RESET TO DEFAULT',
  isSaving = false,
  showSaveAsDefault = false,
  showResetToDefault = false,
  showMoreFilters = false,
  onToggleMoreFilters,
  moreFiltersButtonRef,
}) => {

  return (
    <Box sx={{
      px: 3,
      py: 2.5,
    }}>
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: '8px',
        borderBottom: `1px solid #003771`
      }}>
        <Typography variant="h5"
          sx={{
            fontSize: 18,
            fontWeight: 500,
            color: 'primary.main',
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {onToggleMoreFilters && (
            <Typography
              component="button"
              ref={moreFiltersButtonRef}
              onClick={onToggleMoreFilters}
              sx={{
                fontFamily: 'Montserrat',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '22px',
                color: '#009FDB',
                textTransform: 'capitalize',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',

              }}
            >
              {showMoreFilters ? '− Less Filters' : '+ More Filters'}
            </Typography>
          )}

          <Button
            variant="text"
            onClick={onClose}
            sx={{
              color: '#003771 !important',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: '8px !important',
              minWidth: '36px !important',
              p: 0,
            }}
          >
            <CloseIcon sx={{ fontSize: '24px', color: '#003771 !important' }} />
          </Button>
        </Box>
      </Box>

      {(showSaveAsDefault || showResetToDefault) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: '12px' }}>
          {showResetToDefault && (
            <Button
              variant="text"
              onClick={onResetDefault}
              startIcon={<RefreshOutlinedIcon sx={{ fontSize: '1rem', color: '#39ACE7 !important', transform: 'scaleX(-1)' }} />}
              sx={{
                color: '#39ACE7 !important',
                textTransform: 'uppercase',
                fontWeight: 500,
                fontSize: '0.9rem',
                borderRadius: '8px !important',
                '& .MuiButton-startIcon': {
                  marginRight: '4px',
                }
              }}
            >
              {resetDefaultText}
            </Button>
          )}
          {showSaveAsDefault && (
            <Button
              variant="outlined"
              onClick={onSaveAsDefault}
              disabled={isSaving}
              sx={{
                textTransform: 'uppercase',
                ml: 'auto',
                px: 2,
                py: 0.5,
                fontWeight: 500,
                color: '#39ACE7 !important',
                borderColor: '#39ACE7 !important',
                background: 'transparent !important',
                borderRadius: '8px !important',
                '&.Mui-disabled': {
                  color: '#39ACE7 !important',
                  background: 'transparent !important',
                }
              }}
            >
              {isSaving ? 'SAVING...' : saveText}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DrawerHeader;